import React, { useState } from 'react';
import { Menu, User, LogOut, ChevronDown, ChevronRight } from 'lucide-react';
import logo from '../../assets/img/56.png';

// Componente recursivo para renderizar menús anidados (hasta 3 niveles)
const MenuItem = ({ item, level = 0, activeMenu, setActiveMenu, expandedMenus, toggleExpand }) => {
  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = expandedMenus.includes(item.id);
  const isActive = activeMenu === item.id;

  // Indentación según nivel
  const paddingLeft = 24 + (level * 16);

  // Estilos según nivel
  const getLevelStyles = () => {
    if (isActive) {
      return 'bg-blue-50 border-l-4 border-blue-600 text-blue-600 font-medium';
    }
    if (level === 0) {
      return 'text-gray-800 hover:bg-gray-50 font-medium';
    }
    if (level === 1) {
      return 'text-gray-700 hover:bg-gray-50';
    }
    return 'text-gray-600 hover:bg-gray-50 text-sm';
  };

  const handleClick = () => {
    if (hasChildren) {
      toggleExpand(item.id);
    } else {
      setActiveMenu(item.id);
    }
  };

  return (
    <div>
      <button
        onClick={handleClick}
        className={`w-full text-left py-2.5 pr-4 transition-colors flex items-center justify-between ${getLevelStyles()}`}
        style={{ paddingLeft: `${paddingLeft}px` }}
      >
        <span className="truncate">{item.label}</span>
        {hasChildren && (
          <span className="flex-shrink-0 ml-2">
            {isExpanded ? (
              <ChevronDown size={16} className="text-gray-400" />
            ) : (
              <ChevronRight size={16} className="text-gray-400" />
            )}
          </span>
        )}
      </button>

      {/* Renderizar hijos si está expandido */}
      {hasChildren && isExpanded && (
        <div className="bg-gray-50/50">
          {item.children.map((child) => (
            <MenuItem
              key={child.id}
              item={child}
              level={level + 1}
              activeMenu={activeMenu}
              setActiveMenu={setActiveMenu}
              expandedMenus={expandedMenus}
              toggleExpand={toggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Layout = ({
  children,
  userRole = 'Administrador',
  userName = 'Juan Perez',
  menuItems = [],
  onLogout,
  activeMenu,
  setActiveMenu
}) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expandedMenus, setExpandedMenus] = useState([]);

  // Toggle para expandir/colapsar menús con hijos
  const toggleExpand = (menuId) => {
    setExpandedMenus(prev =>
      prev.includes(menuId)
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  // Función para construir árbol de menús desde array plano
  // Soporta menuItems como array plano con id_padre o como árbol ya construido
  const buildMenuTree = (items) => {
    if (!Array.isArray(items) || items.length === 0) return [];

    // Si ya tiene estructura de árbol (children definido), retornar como está
    if (items[0]?.children !== undefined) {
      return items;
    }

    // Check if we have id_padre (hierarchical structure) - if no item has id_padre, return flat
    const hasHierarchy = items.some(item => item.id_padre != null);
    if (!hasHierarchy) {
      return items.map(i => ({ ...i, children: [] }));
    }

    // Si es array plano con id_padre, construir árbol
    // id_padre es el id_menu del padre (numérico), mientras que 'id' puede ser 'clave' (string)
    // Necesitamos mapear por id_menu Y por clave para encontrar padres
    const itemMapById = {};  // Mapa por id_menu (para buscar padres)
    const itemMapByClave = {};  // Mapa por clave/id
    const roots = [];

    // Primero, crear mapas de todos los items
    items.forEach(item => {
      const normalizedItem = { ...item, children: [] };
      // Usar id como clave principal
      itemMapByClave[item.id] = normalizedItem;
      // Si hay id_menu disponible, también mapear por eso
      if (item.id_menu) {
        itemMapById[item.id_menu] = normalizedItem;
      }
    });

    // Luego, construir jerarquía
    items.forEach(item => {
      if (item.id_padre) {
        // Buscar padre por id_menu
        const parent = itemMapById[item.id_padre];
        if (parent) {
          parent.children.push(itemMapByClave[item.id]);
        } else {
          // Si no encontramos padre, es un root
          roots.push(itemMapByClave[item.id]);
        }
      } else {
        roots.push(itemMapByClave[item.id]);
      }
    });

    // Ordenar por 'orden' si existe
    const sortByOrden = (arr) => {
      arr.sort((a, b) => (a.orden || 0) - (b.orden || 0));
      arr.forEach(item => {
        if (item.children && item.children.length > 0) {
          sortByOrden(item.children);
        }
      });
    };
    sortByOrden(roots);

    // Si hay un único root que es el contenedor del rol (termina en _root),
    // retornar sus hijos como menús principales
    if (roots.length === 1 && roots[0].id && roots[0].id.endsWith('_root')) {
      return roots[0].children || [];
    }

    return roots;
  };

  const menuTree = buildMenuTree(menuItems);

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div
        className={`bg-white shadow-lg transition-all duration-300 ${sidebarOpen ? 'w-64' : 'w-0'
          } overflow-hidden flex-shrink-0`}
      >
        <div className="flex flex-col h-full w-64">
          {/* Header del Sidebar */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <img
                src={logo}
                alt="EBID Logo"
                className="w-10 h-10 object-contain flex-shrink-0"
              />
              <div>
                <h2 className="font-bold text-lg text-gray-800">{userName}</h2>
              </div>
            </div>
          </div>

          {/* Sección de rol */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              <User size={20} className="text-gray-600" />
              <span className="font-semibold text-gray-800">{userRole}</span>
            </div>
          </div>

          {/* Menu Items - Ahora con soporte de 3 niveles */}
          <nav className="flex-1 overflow-y-auto py-2">
            {menuTree.map((item) => (
              <MenuItem
                key={item.id}
                item={item}
                level={0}
                activeMenu={activeMenu}
                setActiveMenu={setActiveMenu}
                expandedMenus={expandedMenus}
                toggleExpand={toggleExpand}
              />
            ))}
          </nav>

          {/* Botón de cerrar sesión */}
          <div className="p-6 border-t border-gray-200">
            <button
              onClick={onLogout}
              className="flex items-center space-x-2 text-gray-700 hover:text-red-600 transition-colors"
            >
              <LogOut size={20} />
              <span>Cerrar Sesión</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <div className="bg-white shadow-sm flex-shrink-0">
          <div className="h-3" style={{ backgroundColor: '#1e3a6f' }}></div>
          <div className="px-6 py-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <Menu size={24} className="text-gray-700" />
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Layout;